import { useState } from "react";
import { esFetch, esPrimedCsrfToken } from "../../common/EsFetch";
import { API_CONTACT, API_JOIN } from "../../common/EsConstants";
import { useEsStrings } from '../../common/EsStrings';

export default function EsHomeContact() {
    const t = useEsStrings();
    // THE JOIN BOX IS A SOFT REGISTER, since 2026-09-17. An address and
    // nothing else (no name since 2026-10-01) lands as an es_account row at
    // role MAILING -- no password, no Cognito user, nothing to log in to. See EsJoinService on esback.
    //
    // Before this it was two uncontrolled inputs and a type="submit" inside a
    // <form> with no onSubmit and no action, which is a full page RELOAD: the
    // fields cleared, nothing was sent, and the whole thing read as a dead
    // button. That is the same class of defect as the Submit beside it, which
    // is an anchor to a route that does not exist -- see the note on the
    // contact form below.
    // THE CONTACT FORM IS A REAL FORM since 2026-09-17. Its Submit was an
    // anchor to /action/contactSend -- a route nothing serves -- so pressing it
    // navigated to escapade's own 404 page and nothing was ever sent. It posts
    // to API_CONTACT now, which mails every administrator and writes a history
    // row whatever SES does. See EsContactController.
    const blankMessage = { name: '', email: '', message: '' };

    const blankJoiner = { email: '' };

    const [ joiner, setJoiner ] = useState( blankJoiner );
    const [ joinMsg, setJoinMsg ] = useState( null );
    const [ joining, setJoining ] = useState( false );

    const [ message, setMessage ] = useState( blankMessage );
    const [ contactMsg, setContactMsg ] = useState( null );
    const [ sending, setSending ] = useState( false );

    const handleMessageChange = ( event ) => {
        const { name, value } = event.target;
        setMessage( { ...message, [ name ]: value } );
    };

    const handleContact = async ( event ) => {
        event.preventDefault();

        if ( sending ) return;

        setSending( true );
        setContactMsg( null );

        try {
            const aResponse = await esFetch( API_CONTACT, {
                method: 'POST',
                headers: {
                    'X-XSRF-TOKEN': await esPrimedCsrfToken(),
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify( message ),
                credentials: 'include'
            } );

            // Parse only what is actually JSON, for the reason EsRegister gives
            // at length -- a throw inside esback answers Spring's whitelabel
            // HTML page, and JSON.parse on HTML rendered as React error #31.
            const aBody = await aResponse.text();
            let aBodyJson = null;

            if ( aBody ) {
                try { aBodyJson = JSON.parse( aBody ); }
                catch { aBodyJson = null; }
            }

            if ( aBodyJson?.msg ) setContactMsg( aBodyJson.msg );
            else if ( !aResponse.ok ) setContactMsg( t( 'home-contact-failed', 'Sorry — that did not go through (error {status}).', { status: String( aResponse.status ) } ) );

            // Cleared only on success, so a refused message is still on screen
            // rather than gone. Losing what somebody wrote is the one thing a
            // contact form must not do.
            if ( aResponse.ok ) setMessage( blankMessage );
        }
        catch {
            // The browser's own error ("Failed to fetch") is in the browser's language, not the page's.
            setContactMsg( t( 'common-server-unreachable', 'Sorry — could not reach the server.' ) );
        }
        finally {
            setSending( false );
        }
    };

    const handleJoinerChange = ( event ) => {
        const { name, value } = event.target;
        setJoiner( { ...joiner, [ name ]: value } );
    };

    const handleJoin = async ( event ) => {
        event.preventDefault();

        // The browser's own "required" is deliberately not used. esback answers
        // one sentence for every outcome it is willing to distinguish, and a
        // native validation bubble would be a second voice saying something
        // slightly different about the same field.
        if ( joining ) return;

        setJoining( true );
        setJoinMsg( null );

        try {
            const aResponse = await esFetch( API_JOIN, {
                method: 'POST',
                headers: {
                    'X-XSRF-TOKEN': await esPrimedCsrfToken(),
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify( joiner ),
                credentials: 'include'
            } );

            // Parse only what is actually JSON, for the reason EsRegister gives
            // at length: a throw inside esback answers Spring's whitelabel HTML
            // page, and JSON.parse on HTML threw a SyntaxError that went
            // straight into the message state and rendered as React error #31.
            const aBody = await aResponse.text();
            let aBodyJson = null;

            if ( aBody ) {
                try { aBodyJson = JSON.parse( aBody ); }
                catch { aBodyJson = null; }
            }

            if ( aBodyJson?.msg ) setJoinMsg( aBodyJson.msg );
            else if ( !aResponse.ok ) setJoinMsg( t( 'home-contact-failed', 'Sorry — that did not go through (error {status}).', { status: String( aResponse.status ) } ) );

            // Cleared only on success, so a refused address is still on screen
            // to correct rather than gone.
            if ( aResponse.ok ) setJoiner( blankJoiner );
        }
        catch {
            // The browser's own error ("Failed to fetch") is in the browser's language, not the page's.
            setJoinMsg( t( 'common-server-unreachable', 'Sorry — could not reach the server.' ) );
        }
        finally {
            setJoining( false );
        }
    };

    return (
        <section id="contact" data-testid="es-home-contact" className="overflow-hidden pt-[50px]">
            <div className="container">
                <div className="border-t border-section-rule pt-12">
                    { /* The title and its line sit where every other section's do, centred
                         above the content, in the same two classes (Tim, 2026-10-12). */ }
                    <div className="sectionHeading wow fadeInUp" data-wow-delay=".1s">
                        <h2 data-testid="es-home-contact-title" className="sectionTitle">
                            { t( 'home-contact-heading', 'Contact Us' ) }
                        </h2>
                        <p data-testid="es-home-contact-blurb" className="bodyText">
                            { t( 'home-contact-blurb', 'We read every message' ) }
                        </p>
                    </div>
                    <div className="-mt-1 flex flex-wrap mx-[-16px]">
                        <div className="w-full px-4 lg:w-8/12">
                            <div
                                className="mb-12 rounded-md p-11 wow fadeInUp sm:p-[55px] lg:mb-5 lg:p-11 xl:p-[55px] max-sm:px-0 pt-0!"
                                data-wow-delay=".15s" >
                                <form data-testid="es-home-contact-form" onSubmit={handleContact}>
                                    <div className="flex flex-wrap mx-[-16px]">
                                        <div className="w-full px-4 md:w-1/2">
                                            <div className="mb-8">
                                                <label htmlFor="name" className="mb-3 block text-sm font-medium text-heading">{ ' ' }{ t( 'home-contact-name', 'Your Name' ) }{ ' ' }</label>
                                                <input autoComplete="name" id = "name"
                                                       data-testid="es-home-contact-name-input"
                                                       name="name"
                                                       value={message.name}
                                                       onChange={handleMessageChange}
                                                       type="text"
                                                       placeholder={ t( 'home-contact-name-placeholder', 'Doesn\'t have to be a real name' ) }
                                                       className="w-full border border-field-edge bg-field rounded-md shadow-one dark:shadow-register py-3 px-6 text-body text-base placeholder-placeholder outline-hidden focus-visible:shadow-none focus:border-focus"
                                                />
                                            </div>
                                        </div>
                                        <div className="w-full px-4 md:w-1/2">
                                            <div className="mb-8">
                                                <label htmlFor="email" className="mb-3 block text-sm font-medium text-heading">{ ' ' }{ t( 'home-contact-email', 'Your Email' ) }{ ' ' }</label>
                                                <input autoComplete="email" id="email"
                                                       data-testid="es-home-contact-email-input"
                                                       name="email"
                                                       value={message.email}
                                                       onChange={handleMessageChange}
                                                       type="email"
                                                       placeholder={ t( 'home-contact-email-placeholder', 'Only if you want a response' ) }
                                                       className="w-full border border-field-edge bg-field rounded-md shadow-one dark:shadow-register py-3 px-6 text-body text-base placeholder-placeholder outline-hidden focus-visible:shadow-none focus:border-focus"
                                                />
                                            </div>
                                        </div>
                                        <div className="w-full px-4">
                                            <div className="mb-8">
                                                <label htmlFor="message" className="mb-3 block text-sm font-medium text-heading">{ ' ' }{ t( 'home-contact-message', 'Your Message' ) }{ ' ' }</label>
                                                <textarea autoComplete="off" id="message"
                                                          data-testid="es-home-contact-message-input"
                                                          name="message"
                                                          value={message.message}
                                                          onChange={handleMessageChange}
                                                          rows="5"
                                                          placeholder={ t( 'home-contact-message-placeholder', 'What\'s on your mind?' ) }
                                                          className="w-full border border-field-edge bg-field rounded-md shadow-one dark:shadow-register py-3 px-6 text-body text-base placeholder-placeholder outline-hidden focus-visible:shadow-none focus:border-focus resize-none"
                                                ></textarea>
                                            </div>
                                        </div>
                                        <div className="w-full px-4">
                                            { /* Submit and the Join button below were the only two
                                                 controls on the page with a text colour and no background
                                                 of their own, so in light mode the label was the entire
                                                 button and hover:shadow-register was what disclosed there
                                                 was a button there at all.

                                                 An earlier pass repaired only the light half -- it added
                                                 text-blue-500 and deliberately did NOT add a ground,
                                                 because giving them one would have changed dark mode and
                                                 that pass was scoped to leave dark mode alone. That
                                                 scope is what has now been lifted: both get the ground,
                                                 both modes change, and the colour pair the label needed
                                                 goes with the ground rather than with the page. See
                                                 buttonSubmit in EsCommon.css.

                                                 inline-flex rather than the anchor's own inline display.
                                                 A background on an inline box is painted round the line
                                                 box, so px-9/py-4 overlap the lines above and below
                                                 instead of making the box taller -- invisible while the
                                                 thing had no background, and the first thing you would
                                                 see once it did. */ }
                                            { /* A SUBMIT, not an anchor. This was
                                                 <a href={ACTION_CONTACT_SEND}> -- a route nothing
                                                 serves -- so the button navigated to escapade's
                                                 own 404 page and the message went nowhere.
                                                 type="submit" inside the form is also what makes
                                                 Enter work, which an anchor never did. */ }
                                            <button
                                                type="submit"
                                                data-testid="es-home-contact-submit-button"
                                                disabled={sending}
                                                className="buttonSubmit inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed" >
                                                {sending ? t( 'home-contact-sending', 'Sending…' ) : t( 'home-contact-submit', 'Submit' )}
                                            </button>
                                            { /* One line under the button. esback answers the same
                                                 sentence whether the mail left or not -- the history
                                                 row is written either way, so a throttled SES is not
                                                 the sender's problem to hear about. */ }
                                            { contactMsg &&
                                                <p data-testid="es-home-contact-message" className="mt-4 text-base font-medium text-body">
                                                    {contactMsg}
                                                </p> }
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="w-full px-4 lg:w-4/12">
                            <div
                                className="relative z-10 mb-5 rounded-md p-8 wow fadeInUp sm:p-11 lg:p-8 xl:p-11 max-sm:px-0 pt-0!"
                                data-wow-delay=".2s" >
                                <h3 data-testid="es-home-join-title" className="mb-4 text-2xl font-bold leading-tight text-heading">{ t( 'home-join-heading', 'Join the mailing list' ) }</h3>
                                { /* WAS LOREM IPSUM, replaced 2026-09-17. The placeholder had
                                     shipped to production and sat under a heading asking people
                                     for their address, which is the one place on the page a
                                     reader has to trust what it says. The copy below is
                                     deliberately plain and Tim's to change. */ }
                                <p data-testid="es-home-join-blurb" className="mb-5 text-base font-medium leading-relaxed text-body" >
                                    { t( 'home-join-blurb', 'News about Escapade. Nothing else.' ) }
                                </p>
                                <form data-testid="es-home-join-form" onSubmit={handleJoin}>
                                    { /* CONTROLLED, and the name attribute is the FORM FIELD NAME
                                         esback binds -- email, matching EsJoinForm. */ }
                                    <label htmlFor="hiddenEmail" hidden>{ t( 'home-join-email-label', 'Email field' ) }</label>
                                    <input autoComplete="email" id="hiddenEmail"
                                           data-testid="es-home-join-email-input"
                                           type="email"
                                           name="email"
                                           value={joiner.email}
                                           onChange={handleJoinerChange}
                                           placeholder={ t( 'home-join-email-placeholder', 'Enter your email' ) }
                                           className="w-full border border-field-edge bg-field rounded-md py-3 px-6 font-medium text-body text-base placeholder-placeholder outline-hidden focus-visible:shadow-none focus:border-focus mb-7" />
                                    { /* The sky-300 border goes with the ground arriving. It was the
                                         only thing marking this out as a control, and an accent outline
                                         drawn round a filled button is the edge of a box that already
                                         has one. outline-hidden and focus-visible:shadow-none are left
                                         exactly as they were -- pre-existing focus behaviour, and not
                                         what this change is about. */ }
                                    <input
                                        type="submit"
                                        data-testid="es-home-join-submit-button"
                                        value={joining ? t( 'home-contact-sending', 'Sending…' ) : t( 'home-join-submit', 'Join the mailing list' )}
                                        disabled={joining}
                                        className="buttonSubmit mb-4 w-full cursor-pointer text-center outline-hidden focus-visible:shadow-none disabled:opacity-50 disabled:cursor-not-allowed" />
                                    { /* One line, and nothing at all until esback has answered.
                                         It used to have an empty state -- "No spam guaranteed." on
                                         es-home-join-note -- which was removed on 2026-09-18.
                                         esback answers the same sentence for a new address, one
                                         already on the list and one belonging to a real account, so
                                         there is nothing here to distinguish -- see
                                         EsJoinService. */ }
                                    { joinMsg &&
                                        <p data-testid="es-home-join-message" className="text-center text-base font-medium leading-relaxed text-body">{joinMsg}</p> }
                                </form>
                                <div className="absolute top-0 left-0 z-[-1]">
                                    { /* look in archive/tailwind.css */ }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}